import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Flame, CheckCircle, Circle, Clock, Target, Zap, ArrowRight, RotateCw, Loader2 } from 'lucide-react';
import { featuresAPI } from '../utils/api';

const WeeklyChallenges = ({ roadmaps = [], user = null }) => {
    const [completedChallenges, setCompletedChallenges] = useState(() => {
        try {
            return JSON.parse(localStorage.getItem('ct_completed_challenges') || '[]');
        } catch { return []; }
    });

    const [expandedChallenge, setExpandedChallenge] = useState(null);
    const [challenges, setChallenges] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    const activeRoadmap = useMemo(() => {
        return roadmaps.find(r => r.isActive) || roadmaps[0];
    }, [roadmaps]);

    // Fetch challenges from backend
    useEffect(() => {
        const fetchChallenges = async () => {
            setLoading(true);
            setError(false);
            try {
                const careerPath = activeRoadmap?.careerPath || null;
                const result = await featuresAPI.getChallenges(careerPath);
                if (result?.challenges && result.challenges.length > 0) {
                    setChallenges(result.challenges);
                } else {
                    setError(true);
                }
                if (result?.completed_ids) {
                    setCompletedChallenges(result.completed_ids);
                }
            } catch (err) {
                setError(true);
            } finally {
                setLoading(false);
            }
        };
        if (roadmaps.length > 0) {
            fetchChallenges();
        } else {
            setLoading(false);
            setError(true);
        }
    }, [activeRoadmap, roadmaps]);

    const handleToggleComplete = async (challengeId) => {
        let updated;
        const challenge = challenges.find(c => c.id === challengeId);
        if (completedChallenges.includes(challengeId)) {
            updated = completedChallenges.filter(id => id !== challengeId);
        } else {
            updated = [...completedChallenges, challengeId];
            // Sync to backend
            try {
                await featuresAPI.completeChallenge(challengeId, challenge?.xp || 0);
            } catch (err) {
                // Continue with local state
            }
        }
        setCompletedChallenges(updated);
        localStorage.setItem('ct_completed_challenges', JSON.stringify(updated));
    };

    const completedThisWeek = challenges.filter(c => completedChallenges.includes(c.id)).length;
    const totalXP = challenges.filter(c => completedChallenges.includes(c.id)).reduce((sum, c) => sum + (c.xp || 0), 0);

    const getDifficultyColor = (diff) => {
        const d = (diff || '').toLowerCase();
        if (d === 'hard') return 'text-red-400 bg-red-500/10';
        if (d === 'medium') return 'text-amber-400 bg-amber-500/10';
        return 'text-emerald-400 bg-emerald-500/10';
    };

    if (roadmaps.length === 0) return null;

    // Loading state
    if (loading) {
        return (
            <div className="bg-[#0F131F] border border-white/5 rounded-2xl p-6">
                <div className="flex items-center gap-2 mb-6">
                    <Flame className="w-5 h-5 text-orange-400" />
                    <h3 className="font-bold text-white text-sm">Weekly Challenges</h3>
                </div>
                <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-6 h-6 text-orange-400 animate-spin" />
                    <span className="ml-2 text-xs text-slate-500">Loading challenges...</span>
                </div>
            </div>
        );
    }

    // Error / empty state
    if (error || !challenges.length) {
        return (
            <div className="bg-[#0F131F] border border-white/5 rounded-2xl p-6">
                <div className="flex items-center gap-2 mb-6">
                    <Flame className="w-5 h-5 text-orange-400" />
                    <h3 className="font-bold text-white text-sm">Weekly Challenges</h3>
                </div>
                <div className="flex flex-col items-center justify-center py-10 text-center">
                    <Target className="w-8 h-8 text-slate-700 mb-3" />
                    <p className="text-sm text-slate-400 mb-1">No challenges available</p>
                    <p className="text-xs text-slate-600">Challenges are generated based on your roadmap. Create one to get started!</p>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-[#0F131F] border border-white/5 rounded-2xl overflow-hidden">
            {/* Header */}
            <div className="p-6 pb-4">
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                        <Flame className="w-5 h-5 text-orange-400" />
                        <h3 className="font-bold text-white text-sm">Weekly Challenges</h3>
                    </div>
                    <div className="flex items-center gap-3 text-xs">
                        <span className="text-slate-500 font-mono">{completedThisWeek}/{challenges.length}</span>
                        <span className="flex items-center gap-1 text-amber-400">
                            <Zap className="w-3 h-3" />
                            {totalXP} XP
                        </span>
                    </div>
                </div>

                {/* Progress bar */}
                <div className="h-1.5 bg-slate-800 rounded-full overflow-hidden mb-2">
                    <motion.div
                        className="h-full bg-gradient-to-r from-orange-500 to-amber-400 rounded-full"
                        initial={{ width: 0 }}
                        animate={{ width: `${(completedThisWeek / Math.max(challenges.length, 1)) * 100}%` }}
                        transition={{ duration: 0.8 }}
                    />
                </div>
            </div>

            {/* Challenges list */}
            <div className="px-6 pb-6 space-y-3">
                <AnimatePresence>
                    {challenges.map((challenge, index) => {
                        const isCompleted = completedChallenges.includes(challenge.id);
                        const isExpanded = expandedChallenge === challenge.id;

                        return (
                            <motion.div
                                key={challenge.id}
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: index * 0.1 }}
                                className={`border rounded-xl transition-all ${isCompleted
                                    ? 'bg-emerald-500/5 border-emerald-500/20'
                                    : 'bg-[#0B0F19] border-white/5 hover:border-white/10'
                                    }`}
                            >
                                <div
                                    className="flex items-center gap-3 p-3 cursor-pointer"
                                    onClick={() => setExpandedChallenge(isExpanded ? null : challenge.id)}
                                >
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleToggleComplete(challenge.id);
                                        }}
                                        className="shrink-0"
                                    >
                                        {isCompleted
                                            ? <CheckCircle className="w-5 h-5 text-emerald-400" />
                                            : <Circle className="w-5 h-5 text-slate-600 hover:text-slate-400" />
                                        }
                                    </button>
                                    <div className="flex-1 min-w-0">
                                        <div className={`text-sm font-medium ${isCompleted ? 'text-emerald-400 line-through' : 'text-white'}`}>
                                            {challenge.title}
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2 shrink-0">
                                        <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded ${getDifficultyColor(challenge.difficulty)}`}>
                                            {challenge.difficulty}
                                        </span>
                                        <span className="text-xs text-amber-400 font-mono">{challenge.xp} XP</span>
                                        <ArrowRight className={`w-3 h-3 text-slate-600 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                                    </div>
                                </div>

                                <AnimatePresence>
                                    {isExpanded && (
                                        <motion.div
                                            initial={{ height: 0, opacity: 0 }}
                                            animate={{ height: 'auto', opacity: 1 }}
                                            exit={{ height: 0, opacity: 0 }}
                                            className="overflow-hidden"
                                        >
                                            <div className="px-3 pb-3 pt-0 ml-8">
                                                <p className="text-xs text-slate-400">{challenge.description || challenge.desc}</p>
                                                {challenge.category && (
                                                    <span className="inline-block mt-2 text-[10px] font-mono text-slate-500 bg-slate-800 px-2 py-0.5 rounded">
                                                        {challenge.category}
                                                    </span>
                                                )}
                                            </div>
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </motion.div>
                        );
                    })}
                </AnimatePresence>
            </div>
        </div>
    );
};

export default WeeklyChallenges;
